<template>
  <v-container class="modern-planner-view">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4">
      <div class="d-flex align-center py-4">
        <v-avatar size="48" color="primary lighten-4" class="mr-4 shadow-sm">
          <v-icon color="primary">fas fa-calendar-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text">Planificador de Planta</div>
          <div class="caption grey--text font-weight-medium uppercase tracking-wider">Cronograma de producción y asignación de carga</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      
      <!-- Premium Clock Widget -->
      <v-card outlined class="clock-widget px-6 py-2 d-flex align-center shadow-sm">
        <div class="text-right mr-4">
          <div class="caption font-weight-black primary--text uppercase">{{ Date.now() | moment('dddd') }}</div>
          <div class="caption grey--text">{{ Date.now() | moment('DD [de] MMMM, YYYY') }}</div>
        </div>
        <v-divider vertical class="mx-2"></v-divider>
        <div class="pl-4">
          <digital-clock :blink="true" class="planner-clock primary--text font-weight-black" />
        </div>
      </v-card>
      
      <v-spacer></v-spacer>
      
      <v-alert v-if="mustSave" dense type="warning" text class="mb-0 mr-4 font-weight-bold shadow-xs">
        <v-icon left x-small>fas fa-save</v-icon> Cambios pendientes de guardar
      </v-alert>
    </v-row>

    <!-- Loading State -->
    <v-progress-linear
      v-show="loading || saving"
      indeterminate
      color="primary"
      rounded
      height="4"
      class="mb-6 shadow-xs"
    />

    <!-- Area Selection Tabs -->
    <v-card outlined class="modern-card elevation-0 mb-6 overflow-hidden">
      <div class="px-6 py-3 grey lighten-4 border-bottom caption font-weight-black grey--text text-uppercase tracking-widest">
        Áreas de Producción Disponibles
      </div>
      <v-tabs
        v-model="tab2"
        centered
        active-class="primary--text font-weight-black active-planner-tab"
        slider-size="4"
        class="planner-tabs"
      >
        <v-tab
          v-for="machine in machines"
          :key="machine.id"
          :value="machine.id"
          class="px-8 text-none body-2"
        >
          <v-icon left x-small class="mr-2">fas fa-industry</v-icon>
          {{ machine.name }}
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Main Content -->
    <v-fade-transition mode="out-in">
      <div v-if="tab2 === null || tab2 === undefined" key="empty" class="text-center py-16">
        <div class="empty-state-planner pa-12 rounded-xl">
          <v-icon x-large color="primary lighten-3" class="mb-6 opacity-40" size="80">fas fa-project-diagram</v-icon>
          <h2 class="text-h5 font-weight-bold grey--text text--darken-2 mb-2">Comience la Planificación</h2>
          <p class="body-1 grey--text text--darken-1">Seleccione un área de producción superior para cargar el cronograma interactivo.</p>
        </div>
      </div>
      
      <v-card v-else :key="tab2" outlined class="modern-card elevation-0 overflow-hidden bg-white">
        <!-- The planner component likely needs a container to manage its scroll/size -->
        <div class="pa-4">
          <machine-planner-alone
            v-if="machines.length > 0 && tab2 !== null && machines[tab2]"
            :machine_id="machines[tab2].id" 
            :dragEnable="true"
            @displayWorkOrder="displayWorkOrder"
          />
        </div>
      </v-card>
    </v-fade-transition>

    <!-- Detail Dialog -->
    <v-dialog v-model="showWorkOrder" max-width="1200" persistent scrollable transition="dialog-bottom-transition">
      <v-card outlined class="modern-card overflow-hidden">
        <work-order-card 
          :work_order="workOrderDisplay" 
          @close="showWorkOrder = false" 
          :loading="workOrderOnDisplayLoading"
        /> 
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DigitalClock from "vue-digital-clock";
import { getWorkOrder, fetchMachines } from '@/api/production'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import notifications from '@/utils/notifications'
import MachinePlannerAlone from '@/components/production/MachinePlannerAlone.vue'

export default {
  name: 'MainPlanner',
  components: { DigitalClock, WorkOrderCard, MachinePlannerAlone },
  data() {
    return {
      machines: [],
      mustSave: false,
      showWorkOrder: false,
      workOrderDisplay: {},
      workOrderOnDisplayLoading: false,
      saving: false,
      loading: true,
      tab2: null,
    }
  },
  async created() {
    this.fetch()
  },
  methods: {
    async displayWorkOrder(item) {
      // Compatibility for different event payloads
      const woId = item.product?.work_order?.id || item.work_order_id
      if (!woId) return

      this.workOrderOnDisplayLoading = true
      this.showWorkOrder = true
      try {
        this.workOrderDisplay = await getWorkOrder(woId)
      } catch (e) {
        notifications.showError('Error al cargar OT: ' + e)
        this.showWorkOrder = false
      } finally {
        this.workOrderOnDisplayLoading = false
      }
    },
    showMustSave() {
      this.mustSave = true
    },
    async fetch() {
      this.loading = true
      try {
        const query = 'enabled=true&ordering=sort'
        const res = await fetchMachines(query)
        this.machines = res
        
        // Auto-select first machine based on company setting or preference
        if (res.length > 0 && this.$store.state.company?.load_planner_on_initial) {
          this.tab2 = 0 // Vue-Tabs index or value? In this case v-tabs is bound to tab2.
          // Correcting: tab2 will store the id of the machine
        }
      } catch (e) {
        notifications.showError('Error al cargar áreas: ' + e)
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    machines: {
      deep: true,
      handler: 'showMustSave'
    }
  }
}
</script>

<style scoped>
.modern-planner-view {
  max-width: 1600px;
}
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 16px !important;
}
.clock-widget {
  border-radius: 14px !important;
  background: white;
  border: 1px solid rgba(0,0,0,0.05);
}
.planner-clock {
  font-size: 2rem;
  letter-spacing: -1px;
  line-height: 1;
}
.active-planner-tab {
  background: rgba(var(--v-primary-base), 0.04);
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.shadow-xs { box-shadow: 0 2px 4px rgba(0,0,0,0.03) !important; }
.empty-state-planner {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px dashed rgba(0,0,0,0.05);
  max-width: 600px;
  margin: 0 auto;
}
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.uppercase { text-transform: uppercase; }
.tracking-wider { letter-spacing: 1px; }
.tracking-widest { letter-spacing: 2px; }
.opacity-40 { opacity: 0.4; }
.gap-3 { gap: 12px; }
</style>